import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import { Button, StyledDiv } from '../../../atoms/button/button';
import { Checkbox } from '../../../atoms/checkbox/checkbox';
import { Select, SelectOption } from '../../../atoms/select/select';
import {
  Spacing,
  Typography,
} from '../../../atoms/progress-with-label/progress-with-label';
import { TextField } from '../../../atoms/text-field/text-field';
import { ISchoolClassInfo, ExamTypes } from '@taleemabad/db';
import { Tabs } from '../../../molecules/tabs/tabs';
import { shifts } from '../../timetable/timetable-assigned-subject';
import { UGEGQuestionFormatSection } from '../ug-eg-select-question-formats/ug-eg-select-question-formats';

// Local string constants — keep in sync with exam-generator/constants.ts
// Do NOT import from app constants to avoid cross-lib dependency.
const QUESTION_TYPE_UNSEEN = 'unseen';
const QUESTION_TYPE_BOTH = 'both';
const FORMAT_SECTION_OBJECTIVE = 'objective';
const FORMAT_SECTION_SUBJECTIVE = 'subjective';

const QUESTION_TYPE_OPTIONS: SelectOption[] = [
  { id: 1, value: 'seen', content: 'Seen Questions' },
  { id: 2, value: QUESTION_TYPE_UNSEEN, content: 'Unseen Questions' },
  { id: 3, value: QUESTION_TYPE_BOTH, content: 'Both' },
];

interface AssessmentTypeConfig {
  value: ExamTypes;
  label: string;
  description: string;
}

// Only AI-service-supported types: generation_type "exam" and "class_assessment".
// Screener (SCREENER) is hidden — not supported by the AI service.
const ASSESSMENT_TYPES: AssessmentTypeConfig[] = [
  {
    value: ExamTypes.TermExam,
    label: 'Exam',
    description: 'Term Papers, Assessments & Bi-Monthly',
  },
  {
    value: ExamTypes.ChapterAssessment,
    label: 'Classroom Practice',
    description: 'Chapters & Units practice tests',
  },
  // Hidden — not supported by AI service (generation_type has no mapping for SCREENER)
  // {
  //   value: ExamTypes.Screener,
  //   label: 'Screener Tool',
  //   description: 'Baseline Assessment',
  // },
];

export interface UGEGClassroomPracticeFormProps {
  classDetails: ISchoolClassInfo | null;
  allClasses?: ISchoolClassInfo[];
  activeTab?: number;
  onTabChange?: (key: number) => void;
  onClassChange?: (classInfo: ISchoolClassInfo) => void;
  assessmentType: ExamTypes;
  onAssessmentTypeChange: (type: ExamTypes) => void;
  questionType: string;
  onQuestionTypeChange: (value: string) => void;
  pageRange: string;
  onPageRangeChange: (value: string) => void;
  pageRangeError?: string;
  sections: UGEGQuestionFormatSection[];
  selectedFormatIds: string[];
  selectedFormatTypes: string[];
  onFormatToggle: (formatId: string, checked: boolean) => void;
  onFormatTypeToggle: (formatTypeId: string, checked: boolean) => void;
  formatCounts?: Record<string, number>;
  onFormatCountChange?: (formatId: string, count: number) => void;
  onGenerateClick: () => void;
  isGenerating?: boolean;
  errorMessage?: string;
}

function buildGradeSelectorContent(
  allClasses: ISchoolClassInfo[] | undefined,
  classDetails: ISchoolClassInfo | null,
  gradeSelectorLabel: string,
  onClassChange?: (cls: ISchoolClassInfo) => void,
): React.ReactNode {
  if (allClasses && allClasses.length > 0) {
    return (
      <Select
        key={classDetails?.gradeSubject ?? 'none'}
        label="Select Grade"
        options={allClasses.map((cls) => ({
          id: cls.gradeSubject,
          value: String(cls.gradeSubject),
          content: `${cls.gradeLabel} - ${cls.section} · ${cls.subjectLabel}`,
        }))}
        defaultValue={classDetails ? String(classDetails.gradeSubject) : null}
        onChange={(option: SelectOption) => {
          const cls = allClasses.find(
            (c) => c.gradeSubject === Number(option.value),
          );
          if (cls && onClassChange) onClassChange(cls);
        }}
        width="100%"
        data-cy="ug-eg-class-select"
      />
    );
  }
  if (allClasses === undefined) {
    return (
      <>
        <SectionLabel>
          <Typography variant="textLabelMediumSurfaceVariant">
            Select Grade
          </Typography>
        </SectionLabel>
        <GradeDisplay>
          <Typography variant="textBodyMediumSurface">
            {gradeSelectorLabel}
          </Typography>
        </GradeDisplay>
      </>
    );
  }
  return (
    <Typography variant="textBodyMediumSurface">No assigned classes</Typography>
  );
}

export const UGEGClassroomPracticeForm: React.FC<
  UGEGClassroomPracticeFormProps
> = ({
  classDetails,
  allClasses,
  activeTab = 1,
  onTabChange,
  onClassChange,
  assessmentType,
  onAssessmentTypeChange,
  questionType,
  onQuestionTypeChange,
  pageRange,
  onPageRangeChange,
  pageRangeError,
  sections,
  selectedFormatIds,
  selectedFormatTypes,
  onFormatToggle,
  onFormatTypeToggle,
  formatCounts = {},
  onFormatCountChange,
  onGenerateClick,
  isGenerating = false,
  errorMessage,
}) => {
  const shouldShowFormats =
    questionType === QUESTION_TYPE_UNSEEN ||
    questionType === QUESTION_TYPE_BOTH;

  const titleLeftText = classDetails
    ? `${classDetails.gradeLabel} - ${classDetails.section}`
    : '';
  const titleRightText = classDetails?.subjectLabel ?? '';
  const gradeSelectorLabel = classDetails
    ? `${classDetails.gradeLabel} - ${classDetails.section} · ${classDetails.subjectLabel}`
    : '';

  const gradeSelectorContent = buildGradeSelectorContent(
    allClasses,
    classDetails,
    gradeSelectorLabel,
    onClassChange,
  );

  const selectedQuestionTypeLabel =
    QUESTION_TYPE_OPTIONS.find((o) => o.value === questionType)?.content ?? '';

  let activeFormatLabel = '';
  if (selectedFormatTypes.length === 2) {
    activeFormatLabel = 'Objective & Subjective format';
  } else if (selectedFormatTypes.includes(FORMAT_SECTION_OBJECTIVE)) {
    activeFormatLabel = 'Objective format';
  } else if (selectedFormatTypes.includes(FORMAT_SECTION_SUBJECTIVE)) {
    activeFormatLabel = 'Subjective format';
  }

  const filteredSections = sections.filter((s) =>
    selectedFormatTypes.includes(s.id),
  );

  const QuestionFormatsPanel = (
    <FormatsPanel>
      <Spacing marginBottom="md">
        <Typography variant="textTitleMediumSurfaceVariant">
          Question Formats
        </Typography>
      </Spacing>

      <FormatTypeCheckboxes>
        {sections.map((section) => (
          <Checkbox
            key={`type-${section.id}`}
            label={section.title}
            checked={selectedFormatTypes.includes(section.id)}
            onChange={(checked) => onFormatTypeToggle(section.id, checked)}
            data-cy={`ug-eg-format-type-checkbox-${section.id}`}
          />
        ))}
      </FormatTypeCheckboxes>

      {filteredSections.map((section) => (
        <Spacing key={section.id} marginTop="md">
          <FormatSectionHeader>
            <Typography variant="textLabelMediumSurface">
              {section.title} Question Types
            </Typography>
          </FormatSectionHeader>
          <StyledDiv flexDirection="column" gap={2}>
            {section.formats.map((format) => {
              const isChecked = selectedFormatIds.includes(format.id);
              return (
                <FormatRow key={format.id}>
                  <Checkbox
                    label={format.label}
                    checked={isChecked}
                    onChange={(checked) => onFormatToggle(format.id, checked)}
                    data-cy={`ug-eg-format-checkbox-${format.id}`}
                  />
                  {isChecked && (
                    <CountInput
                      type="number"
                      min={1}
                      value={formatCounts[format.id] ?? 3}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const raw = e.target.value;
                        if (raw === '') {
                          onFormatCountChange?.(format.id, 0);
                        } else {
                          onFormatCountChange?.(
                            format.id,
                            Math.max(1, Number(raw)),
                          );
                        }
                      }}
                      onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                        const val = Number(e.target.value);
                        if (!val || val < 1) {
                          onFormatCountChange?.(format.id, 1);
                        }
                      }}
                      aria-label={`Count for ${format.label}`}
                      data-cy={`ug-eg-format-count-${format.id}`}
                    />
                  )}
                </FormatRow>
              );
            })}
          </StyledDiv>
        </Spacing>
      ))}

      {errorMessage && (
        <Spacing marginTop="md">
          <Typography variant="textBodySmall" color="var(--md-sys-color-error)">
            {errorMessage}
          </Typography>
        </Spacing>
      )}
    </FormatsPanel>
  );

  return (
    <FormWrapper>
      {/* Shift Tabs */}
      {onTabChange && (
        <Tabs
          variant="secondary"
          activeTabKey={activeTab}
          tabs={shifts}
          onChange={onTabChange}
        />
      )}

      {/* Header */}
      <FormHeader>
        <Typography variant="textTitleLargeOnSurface">Generate Exam</Typography>
        <GradeBreadcrumb>
          <Typography variant="textLabelLargeOnSurfaceVariant">
            {titleLeftText}
          </Typography>
          {titleRightText && (
            <>
              <BreadcrumbSeparator>·</BreadcrumbSeparator>
              <Typography variant="textLabelLargeOnSurfaceVariant">
                {titleRightText}
              </Typography>
            </>
          )}
        </GradeBreadcrumb>
      </FormHeader>

      {/* Single-column content */}
      <ContentGrid showRightPanel={false} data-testid="content-grid">
        {/* Left Column */}
        <LeftColumn>
          {/* Grade Selector */}
          <SectionBlock>{gradeSelectorContent}</SectionBlock>

          {/* Assessment Type */}
          <SectionBlock>
            <SectionLabel>
              <Typography variant="textLabelMediumSurfaceVariant">
                Assessment Type
              </Typography>
            </SectionLabel>
            <AssessmentTypeRow>
              {ASSESSMENT_TYPES.map((type) => (
                <AssessmentCard
                  key={type.value}
                  isActive={assessmentType === type.value}
                  onClick={() => onAssessmentTypeChange(type.value)}
                  data-cy={`ug-eg-assessment-type-${type.value}`}
                >
                  <AssessmentCardIndicator
                    isActive={assessmentType === type.value}
                  />
                  <AssessmentCardText>
                    <Typography
                      variant={
                        assessmentType === type.value
                          ? 'textLabelLargePrimary'
                          : 'textLabelLargeOnSurfaceVariant'
                      }
                    >
                      {type.label}
                    </Typography>
                    <Typography variant="textBodySmallOnSurfaceVariant">
                      {type.description}
                    </Typography>
                  </AssessmentCardText>
                </AssessmentCard>
              ))}
            </AssessmentTypeRow>
          </SectionBlock>

          <>
            {/* Page Range */}
            <SectionBlock>
              <TextField
                label="Page Range"
                placeholder="e.g., 1-10, 10-25"
                value={pageRange}
                onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                  onPageRangeChange(e.target.value)
                }
                supportingText={pageRangeError ?? 'Enter page range i.e 1-20'}
                error={!!pageRangeError}
                data-cy="ug-eg-page-range-input"
              />
            </SectionBlock>
            {/* Question Type + Question Formats (inline below, single column) */}
            <SectionBlock>
              <div>
                <Select
                  key={`question-type-${classDetails?.gradeSubject ?? 'none'}`}
                  label="Question Type"
                  options={QUESTION_TYPE_OPTIONS}
                  defaultValue={questionType || null}
                  onChange={(option: SelectOption) =>
                    onQuestionTypeChange(option.value)
                  }
                  width="100%"
                  data-cy="ug-eg-question-type-select"
                />
                {shouldShowFormats && QuestionFormatsPanel}
              </div>
            </SectionBlock>
          </>
        </LeftColumn>
      </ContentGrid>

      {/* Footer Status Bar + Generate Button */}
      <FormFooter>
        <StatusPills>
          {activeFormatLabel && (
            <StatusPill>
              <Typography variant="textLabelSmallNeutral10">
                {activeFormatLabel}
              </Typography>
            </StatusPill>
          )}
          {selectedQuestionTypeLabel && (
            <StatusPill>
              <Typography variant="textLabelSmallNeutral10">
                {selectedQuestionTypeLabel}
              </Typography>
            </StatusPill>
          )}
        </StatusPills>
        <Button
          data-cy="ug-eg-generate-questions-button"
          onClick={onGenerateClick}
          disabled={
            !questionType ||
            !pageRange ||
            (shouldShowFormats && selectedFormatIds.length === 0) ||
            isGenerating
          }
        >
          {isGenerating ? 'Generating...' : 'Generate Questions'}
        </Button>
      </FormFooter>
    </FormWrapper>
  );
};

/* ── Styled Components ──────────────────────────────────────────────────── */

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding-bottom: 120px; /* space for sticky footer */
`;

const FormHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--rk-spacing-lg) 0 var(--rk-spacing-md) 0;
  border-bottom: 1px solid var(--md-sys-color-outline-variant);
  margin-bottom: var(--rk-spacing-md);
`;

const GradeBreadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: var(--rk-spacing-xs);
`;

const BreadcrumbSeparator = styled.span`
  color: var(--md-sys-color-outline);
  font-size: 14px;
`;

const ContentGrid = styled.div<{ showRightPanel: boolean }>`
  display: grid;
  grid-template-columns: ${({ showRightPanel }) =>
    showRightPanel ? '1fr 1fr' : '1fr'};
  gap: var(--rk-spacing-xl);
  align-items: start;

  ${mq(BreakpointSizes.xs)} {
    grid-template-columns: 1fr;
  }

  ${mq(BreakpointSizes.sm)} {
    grid-template-columns: 1fr;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--rk-spacing-md);
`;

const SectionBlock = styled.div`
  background: var(--md-sys-color-surface-container-high);
  border-radius: var(--rk-spacing-md);
  padding: var(--rk-spacing-lg);
`;

const SectionLabel = styled.div`
  margin-bottom: var(--rk-spacing-sm);
`;

const GradeDisplay = styled.div`
  display: flex;
  align-items: center;
  height: 48px;
  padding: 0 var(--rk-spacing-md);
  background: var(--md-sys-color-surface);
  border: 1px solid var(--md-sys-color-outline-variant);
  border-radius: var(--rk-spacing-sm);
`;

const AssessmentTypeRow = styled.div`
  display: flex;
  gap: var(--rk-spacing-sm);

  ${mq(BreakpointSizes.xs)} {
    flex-direction: column;
  }

  ${mq(BreakpointSizes.sm)} {
    flex-wrap: wrap;
  }
`;

const AssessmentCard = styled.div<{ isActive: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: var(--rk-spacing-sm);
  flex: 1;
  padding: var(--rk-spacing-md);
  border-radius: var(--rk-spacing-sm);
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;

  ${({ isActive }) =>
    isActive
      ? css`
          border: 2px solid var(--md-sys-color-primary);
          background: var(--md-sys-color-surface);
        `
      : css`
          border: 1px solid var(--md-sys-color-outline-variant);
          background: var(--md-sys-color-surface-container-low);
        `}

  &:hover {
    border-color: var(--md-sys-color-primary);
  }
`;

const AssessmentCardIndicator = styled.div<{ isActive: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 3px;
  background-color: ${({ isActive }) =>
    isActive
      ? 'var(--md-sys-color-primary)'
      : 'var(--md-sys-color-outline-variant)'};
`;

const AssessmentCardText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const FormatsPanel = styled.div`
  background: var(--md-sys-color-surface-container-high);
  border-radius: var(--rk-spacing-md);
  padding: var(--rk-spacing-lg);
`;

const FormatTypeCheckboxes = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--rk-spacing-sm);
  margin-bottom: var(--rk-spacing-md);
`;

const FormatRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--rk-spacing-sm);
`;

const CountInput = styled.input`
  width: 56px;
  height: 32px;
  padding: 0 var(--rk-spacing-xs);
  border: 1px solid var(--md-sys-color-outline-variant);
  border-radius: var(--rk-spacing-xs);
  background: var(--md-sys-color-surface);
  color: var(--md-sys-color-on-surface);
  font-size: 14px;
  text-align: center;
  flex-shrink: 0;

  &:focus {
    outline: none;
    border-color: var(--md-sys-color-primary);
  }
`;

const FormatSectionHeader = styled.div`
  background: var(--md-sys-color-secondary-container);
  padding: var(--rk-spacing-sm) var(--rk-spacing-md);
  border-radius: var(--rk-spacing-sm);
  margin-bottom: var(--rk-spacing-sm);
`;

const FormFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--rk-spacing-md) var(--rk-spacing-lg);
  border-top: 1px solid var(--md-sys-color-outline-variant);
  background: var(--md-sys-color-surface);
  position: fixed;
  bottom: 0;
  left: 272px;
  right: 0;
  z-index: 10;

  ${mq(BreakpointSizes.xs)} {
    left: 0;
    flex-direction: column;
    gap: var(--rk-spacing-sm);
    padding: var(--rk-spacing-md);
  }

  ${mq(BreakpointSizes.sm)} {
    left: 0;
  }
`;

const StatusPills = styled.div`
  display: flex;
  align-items: center;
  gap: var(--rk-spacing-sm);
  flex-wrap: wrap;
`;

const StatusPill = styled.div`
  display: flex;
  align-items: center;
  padding: var(--rk-spacing-xs) var(--rk-spacing-sm);
  border-radius: 100px;
  border: 1px solid var(--md-sys-color-outline-variant);
`;
